import type { DateRange } from "@arenko-group/arenko-ui";
import {
  alphabeticalOrder,
  jwt_token,
} from "@arenko-group/arenko-ui/dist/helpers";
import {
  dynamicUrl,
  plotRangeRequest,
} from "@arenko-group/arenko-ui/dist/helpers";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { setSchedulesDataLoading } from "@/features/powerRootSlice";
import {
  initialState,
  url,
  websocketDataHandler,
  websocketDispatchActionHandler,
} from "@/features/powerRootSlice/services/webSocket/websocketConfig";
import { getBaseUrl } from "@/helpers/fetch.helpers";
import logger from "@/helpers/logger";
import type { RootState } from "@/store";
import type { WebsocketMessageType } from "@/types/data/redux.types";
import { setSelectedResource } from "../power.actions";

type WebsocketDataHandler = keyof typeof websocketDataHandler;

type WebsocketDispatchActionHandler =
  keyof typeof websocketDispatchActionHandler;

const sockets: Record<string, Promise<WebSocket>> = {};

/**
 * Registers a new WebSocket instance for a given asset, returning a promise
 * that fulfils on socket open. If socket is already registered then returns
 * the existing instance.
 */
export const getSocket = (assetName: string) => {
  const baseUrl = dynamicUrl(url, assetName);

  if (!(assetName in sockets)) {
    const promise = new Promise<WebSocket>((resolve, reject) => {
      const ws = new WebSocket(baseUrl);

      ws.addEventListener("open", () => {
        resolve(ws);
      });
      ws.addEventListener("error", () => {
        reject(new Error("WebSocket error for Schedule Exchange"));
      });
    });

    sockets[assetName] = promise;
  }
  return sockets[assetName];
};

export const schedulePowerApi = createApi({
  reducerPath: "schedulePowerApi",
  baseQuery: fetchBaseQuery({
    baseUrl: getBaseUrl("/schedule-exchange/"),
    prepareHeaders: (headers) => {
      headers.set("authorization", `Bearer ${jwt_token}`);
      return headers;
    },
  }),
  endpoints: (build) => ({
    getWebsocketMessages: build.query<WebsocketMessageType, string>({
      queryFn: () => ({
        data: initialState,
      }),
      async onCacheEntryAdded(
        arg: string | undefined,
        {
          cacheDataLoaded,
          cacheEntryRemoved,
          updateCachedData,
          dispatch,
          getState,
        }
      ) {
        if (!arg) return;
        const ws = await getSocket(arg);
        try {
          await cacheDataLoaded;
          ws.onmessage = (event: MessageEvent) => {
            const message = JSON.parse(event.data);
            const websocketHandler =
              websocketDataHandler[message.type as WebsocketDataHandler];
            const websocketDispatchHandler =
              websocketDispatchActionHandler[
                message.type as WebsocketDispatchActionHandler
              ];
            if (websocketHandler !== undefined) {
              const state = getState() as unknown as RootState;
              updateCachedData((draft) => {
                websocketHandler({
                  draft,
                  data: message.data,
                  state,
                });
              });
            }
            //sch ex has a lot of repeated data per websocket, such as outputs, so simply dispatch and save to store
            if (websocketDispatchHandler !== undefined) {
              const state = getState() as unknown as RootState;
              websocketDispatchHandler({
                data: message.data,
                dispatch,
                state,
              });
            }
          };
        } catch (err) {
          logger.error(
            `There was an error while handling websocket message for asset ${arg} `,
            err
          );
        }
        await cacheEntryRemoved;
        ws.close();
      },
    }),
    requestData: build.mutation<unknown, string>({
      queryFn: () => ({ data: null }),
      async onCacheEntryAdded(arg, { getState, dispatch }) {
        if (!arg.includes("-noreload")) {
          dispatch(setSchedulesDataLoading(true));
        }
        const state = getState() as unknown as RootState;
        const selectedResource = state?.power?.schedule?.selectedResource;
        const selectedRange = state?.power?.schedule?.selectedRange;

        if (!selectedResource || !selectedRange?.from) {
          return;
        }

        const range: DateRange = {
          from: new Date(selectedRange.from),
          to: new Date(selectedRange.to ?? ""),
        };

        const message = plotRangeRequest(range, selectedRange.type, false);

        try {
          const ws = await sockets[selectedResource];
          ws.send(message);
        } catch (err) {
          logger.error(`Cannot send to socket for ${selectedResource}`, err);
        }
        return;
      },
    }),
    getResources: build.query<string[] | undefined, void>({
      query: () => "resources",
      transformResponse: (response: { resources: string[] }) => {
        return alphabeticalOrder(response.resources) as string[];
      },
      async onCacheEntryAdded(
        _arg,
        { dispatch, getState, cacheDataLoaded, getCacheEntry }
      ) {
        try {
          await cacheDataLoaded;
          const state = getState() as unknown as RootState;
          const { data } = getCacheEntry();
          if (state?.power?.schedule?.selectedResource || !data) return;
          dispatch(setSelectedResource(data[0]));
        } catch (err) {
          logger.error("There was an error while fetching resources ", err);
        }
      },
    }),
  }),
});

export const {
  useGetWebsocketMessagesQuery,
  useRequestDataMutation,
  useGetResourcesQuery,
} = schedulePowerApi;
